/**
 * 되돌릴 수 없는 일을 하기 전에 한 번 멈춰 세우는 창.
 *
 * ⚠ **중복 제출을 여기서 막는다.** 처리 중에는 단추가 잠기고, 창을 닫아도
 * 진행 중인 요청은 취소되지 않는다 — 승인은 서버에서 이미 트랜잭션이 도는
 * 중일 수 있고, 그것을 화면이 취소한 척하면 거짓말이 된다.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button, Notice } from './ui'
import { ApiError, errorMessage } from '@/api/errors'
import { rememberWrite } from '@/auth/pendingWrite'
import { sessionGeneration } from '@/auth/token'

interface Props {
  open: boolean
  title: string
  /** 확인 단추 글자. 「승인」·「거부」처럼 무슨 일이 일어나는지 적는다. */
  action: string
  /** 그만두기 단추 글자. 주지 않으면 'Cancel'(예·아니오로 묻는 창 — 정할 것 45 의 [No]). */
  cancel?: string
  danger?: boolean
  /**
   * 맨 아래 「이 작업은 되돌릴 수 없습니다.」를 붙일지. 주지 않으면 `danger` 를 따른다.
   * ⚠ 되돌릴 수 있는 일(회원 정지 — 해제하면 다시 쓸 수 있다, 직원 비활성·정지, 설정 값)은
   * `danger` 여도 `false` 로 끈다 — 붙이면 거짓 안내가 된다(밤 시험 X-04).
   */
  irreversible?: boolean
  busy: boolean
  error: string | null
  onCancel: () => void
  onConfirm: () => void
  children: ReactNode
  /** 확인 단추를 잠그는 다른 이유 (예: 사유 미입력) */
  disabled?: boolean
  /** 넓은 창(권한 이름 편집·약관 새 판처럼 칸이 많은 창). */
  wide?: boolean
}

export function Confirm({
  open, title, action, cancel, danger, irreversible, busy, error, onCancel, onConfirm, children, disabled, wide,
}: Props) {
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => { if (open) box.current?.focus() }, [open])
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" aria-label={title}>
      <div ref={box} tabIndex={-1}
        className={`max-h-[90vh] w-full overflow-y-auto ${wide ? 'max-w-3xl' : 'max-w-md'} rounded border border-slate-300 bg-white p-5 shadow-lg outline-none dark:border-slate-600 dark:bg-slate-900`}>
        <h2 className="mb-3 text-base font-semibold">{title}</h2>
        <div className="mb-4 text-sm text-slate-700 dark:text-slate-200">{children}</div>
        {error && <div className="mb-3"><Notice tone="stop">{error}</Notice></div>}
        <div className="flex justify-end gap-2">
          <Button tone="plain" onClick={onCancel} disabled={busy}>{cancel ?? 'Cancel'}</Button>
          <Button onClick={onConfirm} disabled={busy || disabled}>
            {/* ⚠ 처리 중임을 글자로도 말한다 — 잠긴 단추만으로는 멈춘 것처럼 보인다. */}
            {busy ? 'Processing…' : action}
          </Button>
        </div>
        {(irreversible ?? danger) && !busy && (
          <p className="mt-3 text-xs text-slate-500">This cannot be undone.</p>
        )}
      </div>
    </div>
  )
}

/**
 * 쓰기 한 번의 상태 — 처리 중 · 오류 · 중복 방지.
 *
 * ⚠ **`busy` 를 ref 로도 든다.** 상태만으로 막으면 같은 순간에 두 번 눌린 클릭이
 * 둘 다 통과한다 — 다시 그려지기 전이라 둘 다 `busy === false` 를 본다.
 */
export function useWrite() {
  const [busy, setBusy] = useState(false)
  const running = useRef(false)
  const [error, setError] = useState<string | null>(null)

  async function run(fn: () => Promise<void>): Promise<void> {
    if (running.current) return
    running.current = true
    setBusy(true)
    setError(null)
    try {
      await fn()
    } finally {
      running.current = false
      setBusy(false)
    }
  }

  return { busy, error, setError, run }
}

export type WriteState = ReturnType<typeof useWrite>

/**
 * 창 안의 쓰기 한 번 — 보내고, 끝나면 `onDone`, 실패하면 창 안 빨간 안내(명세 4-4).
 *
 * ⚠ **PIN 에 막힌 쓰기는 기억한다**(명세 1절 머리말 — 레거시 그대로). 키패드를 지나면
 * 한 번 다시 보낸다. 다른 실패는 다시 보낸다고 답이 안 바뀐다 — 특히 「이미 처리된
 * 신청」을 자동으로 다시 보내면 아무 일도 안 하면서 뭔가 된 것처럼 보인다.
 * 기억하지 않는 예외(사고 처리·내 계정)는 `remember: false` 로 부른다.
 */
export async function submitWrite(
  w: WriteState,
  label: string,
  send: () => Promise<void>,
  onDone: () => void,
  opts: WriteOptions = {},
): Promise<void> {
  await w.run(() => sendWrite(w, label, send, onDone, opts))
}

export interface WriteOptions {
  /** PIN 에 막힌 쓰기를 기억할지(기본 기억). */
  remember?: boolean
  /** 창 안 문구를 고르는 자리(기본 명세 4-4 `errorMessage`). */
  message?: (err: unknown) => string
  /** 실패한 뒤 할 일(예: 409 면 상세 다시 읽기). */
  onError?: (err: unknown) => void
}

/**
 * [submitWrite] 의 몸통 — 이미 `w.run` 안에 있을 때(먼저 번호를 찾아야 하는 창) 직접 부른다.
 */
export async function sendWrite(
  w: WriteState,
  label: string,
  send: () => Promise<void>,
  onDone: () => void,
  opts: WriteOptions = {},
): Promise<void> {
  // ⚠ **보내기 전에 세션 세대를 읽는다.** 답을 기다리는 사이 세션이 끝날 수 있다
  // (다른 요청의 401). 그 뒤에 도착한 403 `pin_required` 를 지금 세대로 기억하면
  // 다음 세션에서 나간다 — 이 쓰기를 한 세션에 묶는다(pendingWrite.ts).
  const generation = sessionGeneration()
  try {
    await send()
    onDone()
  } catch (err) {
    if (opts.remember !== false && err instanceof ApiError && err.kind === 'pin_required') {
      rememberWrite({ label, run: send }, generation)
      return
    }
    w.setError(opts.message ? opts.message(err) : errorMessage(err))
    opts.onError?.(err)
  }
}

/** 보기만 하는 창 — 「닫기」 하나(약관 판 보기 EA-52). */
export function ViewDialog({ open, title, onClose, children }: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
}) {
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => { if (open) box.current?.focus() }, [open])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" aria-label={title}>
      <div ref={box} tabIndex={-1}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded border border-slate-300 bg-white p-5 shadow-lg outline-none dark:border-slate-600 dark:bg-slate-900">
        <h2 className="mb-3 text-base font-semibold">{title}</h2>
        <div className="mb-4 text-sm text-slate-700 dark:text-slate-200">{children}</div>
        <div className="flex justify-end">
          <Button tone="plain" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  )
}
